/**
 * Archivo: facturasai.service.ts
 * Descripción: Este archivo contiene la definición del servicio FacturasIAService, el cual se utiliza para realizar operaciones relacionadas con el reconocimiento de la información en las facturas.
 * Fecha de creación: 17/02/2024
 * Autor: José Rodríguez
 * Comentarios: Utiliza el servicio web API REST FacturasIAController.
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService } from '../base';
import { MatSnackBar } from '@angular/material/snack-bar'; 
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { DatosConvertidosEnFactura, ReconocimientoFacturaDTO } from '../../models/ia/facturas';
import { environment } from '../../../environments/environment';
import { ApiResponse, ExceptionGeneral } from '../../models/base';
import { concatMap, catchError, finalize, map, tap } from 'rxjs/operators';

const FACTURASIA_ENDPOINT = '/ia/facturas/';

@Injectable({ providedIn: 'root' })
export class FacturasIAService extends BaseService {
  // #region Constantes
  _ERROR_NO_CONTENT = 'No se ha obtenido contenido del archivo';
  _ERROR_NO_CONTACT = 'No se ha reconocido el proveedor';
  _ERROR_NO_PRIMARY_TAX_PROVIDER = 'No se ha reconocido el primer impuesto del proveedor';
  _ERROR_NO_SECONDARY_TAX_PROVIDER = 'No se ha reconocido el segundo impuesto del proveedor';
  _ERROR_NO_ACCOUNT = 'No se ha reconocido la cuenta de cargo del proveedor';
  _ERROR_NO_INVOICE_LINES = 'No se han generado las líneas de la factura';
  _ERROR_NO_NUMBER = 'No se ha reconocido el número de la factura';
  _ERROR_NO_DATE = 'No se ha reconocido la fecha de la factura';
  _ERROR_NO_RELATION_TYPEPAID = 'No se ha encontrado la relación entre el tipo de pago y la factura reconocida';
  // #endregion

  // #region Propiedades
  private listaSinContabilizar: ReconocimientoFacturaDTO[] = [];
  lsc$: BehaviorSubject<ReconocimientoFacturaDTO[]> = new BehaviorSubject<ReconocimientoFacturaDTO[]>([]);
  listaSinContabilizar$: Observable<ReconocimientoFacturaDTO[]> = this.lsc$.asObservable();

  private listaContabilizadas: ReconocimientoFacturaDTO[] = [];
  lc$: BehaviorSubject<ReconocimientoFacturaDTO[]> = new BehaviorSubject<ReconocimientoFacturaDTO[]>([]);
  listaContabilizadas$: Observable<ReconocimientoFacturaDTO[]> = this.lc$.asObservable();

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // #endregion

  constructor(
    private httpClient: HttpClient,
    protected snackBar: MatSnackBar
  ) {
    super(snackBar);
  }

  // #region Métodos relacionados con el reconocimiento de la información en las facturas. Llamadas a la API REST.

  /**
   * Actualiza el proveedor de una factura en el servidor.
   *
   * @param guidFichero - El identificador único del fichero de la factura.
   * @param contactoId - El identificador del contacto del proveedor.
   * @returns Un Observable que emite un objeto ReconocimientoFacturaDTO o null.
   */
  actualizarProveedor(guidFichero: string, contactoId: number): Observable<ReconocimientoFacturaDTO | null> {
    this.loading$.next(true);

    return this.httpClient
      .get<ReconocimientoFacturaDTO | null>(`${environment.apiUrl}${FACTURASIA_ENDPOINT}provider/update/${guidFichero}/${contactoId}`)
      .pipe(
        tap((data: ReconocimientoFacturaDTO | null) => {
          if (data != null) {
            if (data.error && data.error.trim() === '') {
              this.mostrarMensajeGeneral('La factura se ha contabilizado correctamente.', 'success');
            } else {
              this.mostrarMensajeGeneral('La factura no se ha contabilizado correctamente. Compruebe de nuevo los errores encontrados', 'warning');
            }

            this.obtencionListaSinContabilizar();
          }
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Marca una factura como eliminada en el servidor.
   *
   * @param guidFichero - El identificador único del fichero de la factura.
   * @returns Un Observable que emite un booleano.
   */
  marcarComoEliminada(guidFichero: string): Observable<boolean> {
    this.loading$.next(true);

    return this.httpClient
      .get<any>(`${environment.apiUrl}${FACTURASIA_ENDPOINT}markDeleted/${guidFichero}`)
      .pipe(
        map((data: any) => {
          if (data == null) {
            return false;
          }

          this.obtencionListaSinContabilizar();
          this.mostrarMensajeGeneral('La factura se ha marcado como eliminada correctamente.', 'success');

          return true;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Intenta regenerar una factura en el servidor.
   *
   * @param guidFichero - El identificador único del fichero de la factura.
   * @returns Un Observable que emite un booleano.
   */
  regenerarFactura(guidFichero: string): Observable<boolean> {
    this.loading$.next(true);

    return this.httpClient
      .get<any>(`${environment.apiUrl}${FACTURASIA_ENDPOINT}regenerate/${guidFichero}`)
      .pipe(
        map((data: any) => {
          if (data == null) {
            return false;
          }

          this.obtencionListaSinContabilizar();
          this.mostrarMensajeGeneral('La factura se ha generado correctamente.', 'success');

          return true;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Obtiene la lista de facturas contabilizadas.
   */
  obtencionListaContabilizadas(): void {
    this.httpClient.get<ApiResponse<ReconocimientoFacturaDTO[]> | ExceptionGeneral>(`${environment.apiUrl}${FACTURASIA_ENDPOINT}accounted`)
      .subscribe({
        next: (data: ApiResponse<ReconocimientoFacturaDTO[]> | ExceptionGeneral) => {
          if ((data as ExceptionGeneral).error) {
            this.loading$.next(false);
            this.procesarErrorGeneral(data as ExceptionGeneral);
          } else {
            this.listaContabilizadas = (data as ApiResponse<ReconocimientoFacturaDTO[]>).data;
            this.lc$.next(this.listaContabilizadas);
          }
        },
        error: (error) => {
          this.procesarError(error);
        }
      });
  }

  /**
   * Obtiene la lista de facturas sin contabilizar.
   */
  obtencionListaSinContabilizar(): void {
    this.loading$.next(true);

    this.httpClient.get<ApiResponse<ReconocimientoFacturaDTO[]> | ExceptionGeneral>(`${environment.apiUrl}${FACTURASIA_ENDPOINT}unaccounted`)
      .subscribe({
        next: (data: ApiResponse<ReconocimientoFacturaDTO[]> | ExceptionGeneral) => {
          if ((data as ExceptionGeneral).error) {
            this.loading$.next(false);
            this.procesarErrorGeneral(data as ExceptionGeneral);
          } else {
            this.listaSinContabilizar = (data as ApiResponse<ReconocimientoFacturaDTO[]>).data;
            this.lsc$.next(this.listaSinContabilizar);

            if (this.listaSinContabilizar.length == 0) {
              this.reconocerFacturasPorCliente();
            } else {
              this.loading$.next(false);
            }
          }
        },
        error: (error) => {
          this.procesarError(error);
          this.loading$.next(false);
        }
      });
  }

  /**
   * Realiza el reconocimiento de la información en una factura a partir de un fichero.
   * @param guid Identificador único del fichero.
   * @returns Observable<DatosConvertidosEnFactura>
   */
  reconocerFacturaPorFichero(guid: string): Observable<DatosConvertidosEnFactura> {
    this.loading$.next(true);

    return this.httpClient
      .get<DatosConvertidosEnFactura | ExceptionGeneral>(`${environment.apiUrl}${FACTURASIA_ENDPOINT}extract/${guid}`)
      .pipe(
        map((data: DatosConvertidosEnFactura | ExceptionGeneral) => {
          if ((data as ExceptionGeneral).error) {
            this.procesarErrorGeneral(data as ExceptionGeneral);
            throw data;
          }

          return data as DatosConvertidosEnFactura;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Descarga un fichero a partir de su identificador único.
   * @param guid Identificador único del fichero.
   */
  verArchivo(guid: string): void {
    this.httpClient.get<ApiResponse<string>>(`${environment.apiUrl}${FACTURASIA_ENDPOINT}urlamazon/${guid}`)
      .subscribe({
        next: (data: ApiResponse<string>) => {
          if (data != null && data.data != null && data.data.trim() != '') {
            window.open(data.data, '_blank');
          }
        },
        error: (error) => {
          this.procesarError(error);
        }
      });
  }

  // #endregion

  // #region Métodos privados

  /**
   * Realiza el reconocimiento de facturas por cliente. Si se devuelven factura reconocidas, se vuelve a llamar a la API para obtener las facturas sin contabilizar.
   */
  private reconocerFacturasPorCliente(): void {
    this.httpClient.get<any>(`${environment.apiUrl}${FACTURASIA_ENDPOINT}extractbyclient`)
      .pipe(
        concatMap((data: any) => {
          if (data != null) {
            return this.httpClient.get<ApiResponse<ReconocimientoFacturaDTO[]> | ExceptionGeneral>(
              `${environment.apiUrl}${FACTURASIA_ENDPOINT}unaccounted`
            );
          } else {
            return of(null);
          }
        }),
        concatMap((data: ApiResponse<ReconocimientoFacturaDTO[]> | ExceptionGeneral | null) => {
          if (data == null) {
            return of(null);
          } else {
            if ((data as ExceptionGeneral).error) {
              this.loading$.next(false);
              this.procesarErrorGeneral(data as ExceptionGeneral);
            } else {
              this.listaSinContabilizar = (data as ApiResponse<ReconocimientoFacturaDTO[]>).data;
              this.lsc$.next(this.listaSinContabilizar);
            }

            return this.httpClient.get<ApiResponse<ReconocimientoFacturaDTO[]> | ExceptionGeneral>(
              `${environment.apiUrl}${FACTURASIA_ENDPOINT}accounted`
            );
          }
        })
      )
      .subscribe({
        next: (data: ApiResponse<ReconocimientoFacturaDTO[]> | ExceptionGeneral | null) => {
          if (data == null) {
            this.loading$.next(false);
            return;
          }

          if ((data as ExceptionGeneral).error) {
            this.loading$.next(false);
            this.procesarErrorGeneral(data as ExceptionGeneral);
          } else {
            this.listaContabilizadas = (data as ApiResponse<ReconocimientoFacturaDTO[]>).data;
            this.lc$.next(this.listaContabilizadas);
          }
        },
        error: (error) => {
          this.procesarError(error);
          this.loading$.next(false);
        },
        complete: () => {
          this.loading$.next(false);
        },
      });
  }

  // #endregion
}